import { fetcher } from '#lib/backendFetcher';

/**
 * Exports companies and contacts of a prospect query as a CSV file.
 * @param {string} prospectQueryId - UUID of the prospect query
 * @param {Object} [options]
 * @param {Array<string>} [options.columns] - Array of columns to export
 * @param {Array<string>} [options.rolePriority] - Ordered role keys (may include 'company');
 *   collapses the export to one recipient per company, companies without a match are omitted
 * @param {'email'|'post'} [options.reachableBy] - Only export recipients reachable via this channel
 * @param {Array<string>} [options.leadIds] - Export only these companies (globalCompanyIds, max 500)
 * @param {true} [options.selectAll] - Export every company of the query matching options.filters
 * @param {Object} [options.filters] - Leads-table filters for selectAll
 * @param {Array<string>} [options.contactIds] - Export only these contacts (global contact IDs)
 *   Exactly one of leadIds, selectAll or contactIds.
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const exportProspectQueryToCSV = async (prospectQueryId, options = {}) => {
	const response = await fetcher(`/api/v1/leads/export/${prospectQueryId}`, {
		method: 'POST',
		body: options,
		responseType: 'stream',
	});

	// Extract filename from Content-Disposition header if available
	const contentDisposition = response.headers.get('Content-Disposition');
	let filename = 'export.csv';
	if (contentDisposition) {
		const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
		if (filenameMatch) {
			filename = filenameMatch[1];
		}
	}

	// Get blob from response
	const blob = await response.blob();

	return { blob, filename };
};

/**
 * Downloads a CSV file to the user's computer.
 * @param {Blob} blob - CSV file as Blob
 * @param {string} filename - Filename for the download
 * @returns {void}
 */
export const downloadCSVFile = (blob, filename) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};
